---
title: Why Alexa couldn't help me find a more creative title
date: '2018-04-14'
excerpt: >-
  Why creativity beats automation, and how leaders and organizations can
  intentionally create room for more creative breakthroughs at work. Learn which
  human qualities foster new ideas and practical strategies to boost team
  creativity.
metaTitle: Why Alexa Couldn't Help Me Find a More Creative Title - EmpowerMensch
metaDescription: >-
  Explore the limits of digital automation and discover proven techniques for
  nurturing creativity in yourself and your organization.
language: en
slug: why-alexa-couldnt-help-me-find-a-more-creative-title
author: Kevin Rassner
---

A recently published Bitkom study warns explicitly against a massive loss of jobs due to digitalization. The reasons are obvious: machines are much better than people in repetitive tasks with clearly definable goals. Even seemingly complex tasks such as driving a car can be presented as a chain of repetitive tasks (lateral and longitudinal control) with clearly definable goals (no accidents). People will therefore have to focus on activities that require human skills that are difficult or impossible to digitize, especially creativity.

> The capabilities of the average human being for creativity, for growth, for [collaboration](/blog/revisiting-team-building-in-2025), for productivity are far greater than we yet have recognized.
>
>  - Douglas McGregor, Founder of [modern management theory](/blog/y-tho-why-it-is-now-time-for-new-management-methods)

But how can creativity develop and how can companies find ways to promote it?

## How does creativity arise?

The bad news first: creativity cannot be planned. The good news is that you know what qualities and activities help people to be creative. I personally find the following points particularly important:

### 1. Daydreaming

Psychologist Rebecca L. McMillian sees daydreaming as a great engine for creativity. Forgetting time often results in creative phases.

Creative people also need time alone. To be at rest also means not to be cognitively challenged. Many people report creative ideas while taking a shower or taking a train.

> The Muses demand solitude, and nothing chases them away more than the tumult.
>
>  - Gotthold Ephraim Lessing, German poet

### 2. Stimulation

The unplanned, unfiltered intake of information and stimuli is often food for creative thoughts. The stranger the impressions and the newer the experiences, the more interesting the resulting ideas.

> I have no particular talent, I'm just passionately curious.
>
>  - Albert Einstein, Physicist

### 3. Observation

Observing other people often triggers creative processes. It has been proven that looking at beautiful things can also inspire creativity. A historically prominent example of inspiration from people and beauty are the muses (male or female), which often inspired many artists to new works.

What I find interesting here is that the words music, museum, amusement, and musing are all derived from the word muse.

> Try to be one of the people on whom nothing is lost!
>
>  - Henry James, Author

### 4. Revolution

Creativity is often triggered by questioning incontrovertibly presumed patterns of thought and assumptions. In other words, experience often limits creativity. A popular product design game is therefore the elimination of facts (what would be the ideal toaster if bread was liquid?)

> You can never solve problems with the same way of thinking that caused them.
>
>  - Albert Einstein

### 5. Risk

Creative people are also taking more risks. Albert Einstein was an enthusiastic sailor, even if he could not swim.

Making mistakes and being creative also belong together. A deficient error culture can therefore very much limit creativity (this point is also the root of the evil, why children gradually lose their creativity through classical school education).

> Creatives fail and the really good ones fail often.
>
>  - Steven Kotler, Author

The question now is whether and how such framework conditions can be represented in the working world. I would like to distinguish between two scenarios. There are concrete situations in which you (as a leader) want to trigger a creative process in your team or in yourself. However, there are also constant framework conditions to increase creativity in daily work. The latter are important, but not revolutionary. Companies like Google show what an environment can look like that breaks through patterns of thought, provides unplanned stimuli, allows new experiences and is aesthetic at the same time.

I would therefore like to focus on the first category. How can framework conditions be created to release special creative forces in project phases or workshops?

## Project procurement: 1 sofa, 5 nerfguns.

Agilist and management consultant Alexander Krause reports in one of his lectures on his attempt to provide his team with the distance, distraction and excitement that creativity needs. On project budget he ordered a sofa and several nerfguns, with which the meeting room, which had been converted into a mob programming room, could become more than just a workplace. Such outbreaks from the standard procedure are very valuable, especially for major technical challenges that require creative approaches.

Mob Programming also promotes creativity. The division into a driver (programmer) and several navigators (decision-makers) allows active and passive phases to be alternated very well. There is also a place for observing people and daydreaming.

## Breaking Out – mentally and geographically

Humans are creatures of habit - with all their advantages and disadvantages. Thinking and acting in patterns is a particular disadvantage when it comes to finding new approaches to problems. Even if we consciously move away from the trampled paths in our search for a good idea, our experiences and assumptions keep pushing us back. To break through this process, it is very helpful to leave as many of the old framework conditions behind as possible. The most obvious solution here is to move geographically to an unusual location. Instead of trying your luck in the office or in the meeting room, you choose a place that is as inspiring as possible. Interested in what the whole thing can look like? Then read [this article](/blog/creative-workshop-night-at-the-museum), which is about a creative workshop that my team and I held in a museum.

## To the solution that way

Current research increasingly links creativity to movement. The background is now obvious: one pursues a cognitively not demanding task and thus has time for daydreaming or observing. Stanford scientist Marily Oppezzo used several experiments to impressively demonstrate that people are more creative by simply walking. An effect that could even be measured in the second attempt when the test persons had only run in the first attempt.

This insight can be easily integrated into the business context.

Many companies have more than one building, based on the idea of the café meeting (unfortunately, I forgot the source), where you interrupt 30-45-minute meeting blocks to cycle to another café in the city, you can also prepare special meeting events on the factory site: 30-45 minutes of meeting in the first meeting room. Then you take the factory bike or walk to the next building. There you have to try out the coffee corner, do some networking, before you continue with the next block. This implementation meets many of the above requirements that encourage creativity and can be profitably applied before meetings that require significant creative effort.

Meetings that do not require any documents (computers, pens, beamers) can also be held completely on foot. It is advisable to follow Oppezzo's advice and record upcoming ideas quickly with the smartphone.

## Closing Words

As so often, the approaches declared as [modern management methods](/blog/y-tho-why-it-is-now-time-for-new-management-methods) are neither rocket science nor revolutionary. The revolution, on the other hand, must take place in people's minds. If previous assumptions are questioned, many new implementations can be found: If we didn't have an office today, where would we meet for a special occasion? If we didn't have a fixed seat, where would we settle in for the project?

